/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import * as Utils from '../../helpers/helpers';

@Component({
  selector: 'wfm-filter-tag-panel',
  templateUrl: './filter-tag-panel.component.html',
  styleUrls: ['./filter-tag-panel.component.scss']
})
export class FilterTagPanelComponent implements OnChanges {
  private window: Window;
  private _oldPanelWidth: number;
  private _resizeEventId: number;
  isMultiLine = false;
  isCollapsed = true;
  lineHeight: number = undefined;
  showToggle = false;

  @Input() isScrollToEnabled = false;
  @Input() filterTags: FilterTag[] = [];
  @Input() headerLabel: string;
  @Input() showAllTags: boolean;
  @Input() displayOnlyLabels = false;
  @Output() removeTag = new EventEmitter<RemovedFilterTag>();
  @Output() scrollTo = new EventEmitter<string>();

  @ViewChild('tagContainer')
  tagContainer: ElementRef<HTMLDivElement>;

  @ViewChild('tagPanel')
  tagPanel: ElementRef<HTMLDivElement>;

  constructor(@Inject(DOCUMENT) document: any, private cd: ChangeDetectorRef) {
    this.window = document.defaultView;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showAllTags) {
      this._evaluateFlags();
    }
    if (changes.filterTags) {
      this._refreshFilterList();
    }

    if (this._isCollapsingMode()) {
      // Determine the initial (old) panel width.
      if (this.filterTags.length > 0) {
        if (this._oldPanelWidth === undefined) {
          this.window.setTimeout(() => {
            this._oldPanelWidth = this.tagPanel?.nativeElement?.clientWidth;
          });
        }
      } else {
        this._oldPanelWidth = undefined;
      }
    }
  }

  scrollToValue(key: string) {
    this.scrollTo.emit(key);
  }

  onRemoveTag(key: string, value?) {
    this.removeTag.emit({
      key,
      value,
      isRemoveAll: false
    });
  }

  onClearAll() {
    this.filterTags = [];
    this.removeTag.emit({
      isRemoveAll: true
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    // The tagPanel will only exist when there are tags to display.
    if (this._isCollapsingMode() && this.tagPanel) {
      if (this._resizeEventId) {
        this.window.clearTimeout(this._resizeEventId);
      } else {
        this._onResizeStart();
      }
      this._resizeEventId = this.window.setTimeout(() => {
        this._onResizeEnd();
      }, 500);
      this._onResize(this.tagPanel?.nativeElement?.clientWidth);
    }
  }

  private _refreshFilterList(): void {
    this._evaluateFlags();
  }

  private _evaluateFlags(): void {
    if (this._isCollapsingMode()) {
      this.window.setTimeout(() => {
        const firstElement: any = this.tagContainer?.nativeElement?.firstElementChild;
        const lastElement: any = this.tagContainer?.nativeElement?.lastElementChild;

        this.lineHeight = firstElement?.offsetHeight;

        const isMultiLine =
          !!firstElement &&
          !!lastElement &&
          firstElement.offsetTop < lastElement.offsetTop &&
          firstElement.offsetTop + firstElement.offsetHeight < lastElement.offsetTop + lastElement.offsetHeight;
        if (this.isMultiLine !== isMultiLine) {
          this.isMultiLine = isMultiLine;
          this.isCollapsed = true;
          // When resizing from a smaller to larger window, we hide the toggle button so the space can be taken
          // up by filter tags and we can more accurately determine if multiple lines are necessary.
          if (this.isMultiLine && !this._isResizing()) {
            this.showToggle = true;
          }
          this.cd.detectChanges();
        }
      }, 50);
    }
  }

  private _isResizing(): boolean {
    return this._resizeEventId !== undefined;
  }

  private _onResize(newPanelWidth: number): void {
    Utils.assert(this._isCollapsingMode(), 'Should only handle resize in collapsing mode.');
    // We want to show the toggle button if the panel is being sized smaller.  Otherwise we temporarily hide
    // it until the sizing is done.
    const showToggle = this._oldPanelWidth >= newPanelWidth;
    if (this.showToggle !== showToggle) {
      this.showToggle = showToggle;
      this.cd.detectChanges();
    }
    this._oldPanelWidth = newPanelWidth;
    this._evaluateFlags();
  }

  private _onResizeEnd(): void {
    Utils.assert(this._isCollapsingMode(), 'Should only handle resize in collapsing mode.');
    if (this.isMultiLine) {
      this.showToggle = true;
      this.cd.detectChanges();
    }
  }

  private _onResizeStart(): void {
    Utils.assert(this._isCollapsingMode(), 'Should only handle resize in collapsing mode.');
    // Nothing specific to do for now.
  }

  private _isCollapsingMode(): boolean {
    return !this.showAllTags;
  }
}

export interface RemovedFilterTag {
  key?: string;
  value?: any;
  isRemoveAll?: boolean;
}

export interface FilterTag {
  hideCloseButton?: boolean;
  key: string;
  label: string;
  values?: any[];
}
